import moment from 'moment'
import jwtDecode from 'jwt-decode'

import { userService } from '@/service/user.service'
import router from '@/router'

const isTokenValid = token => {
    return token && moment.unix(jwtDecode(token).exp).isAfter()
}

let isRefreshingToken = null

const state = {
    jwt: JSON.parse(localStorage.getItem('JWT')),
    jwtOverwritten: JSON.parse(localStorage.getItem('JWT-overwritten')),
    backend: JSON.parse(localStorage.getItem('backend')),
    userInfo: JSON.parse(localStorage.getItem('userInfo')),
    isAuthenticating: false,
}

const getters = {
    defaultTab(state, getters) {
        return getters.getCurrentUser?.default_tab || 'map'
    },
    getCurrentUser(state) {
        return state.jwt?.access ? jwtDecode(state.jwt?.access) : null
    },
    getCurrentUserId(state, getters) {
        return getters.getCurrentUser?.user_id
    },
    hasActivityAccess(state) {
        return state.userInfo?.platform === 'alplora'
    },
    isAxRoad(state) {
        return state.userInfo?.platform === 'ax-road'
    },
    hasAdvancedRuleAccess(state, getters) {
        return !!getters.getCurrentUser?.advanced_rule_access
    },
    hasDashboardAccess(state, getters) {
        return !!getters.getCurrentUser?.dashboard_access
    },
    hasDebugAccess(state, getters) {
        return !!getters.getCurrentUser?.debug_access
    },
    hasEcoScoreAccess(state, getters) {
        return !!getters.getCurrentUser?.ecoscore_access
    },
    hasLocationHistoryAccess(state, getters) {
        return !!getters.getCurrentUser?.location_history_access
    },
    hasLocationManagementAccess(state, getters) {
        return !!getters.getCurrentUser?.location_management_access
    },
    hasLsvaAccess(state, getters) {
        return !!getters.getCurrentUser?.lsva_access
    },
    hasLsvaAdminAccess(state, getters) {
        return !!getters.getCurrentUser?.lsva_admin_access
    },
    hasMaintenanceAccess(state, getters) {
        return !!getters.getCurrentUser?.maintenance_access
    },
    hasMapAccess(state, getters) {
        return !!getters.getCurrentUser?.map_access
    },
    hasNavigationAccess(state, getters) {
        if (
            !process.env.VUE_APP_NAVIGATION_USERS ||
            !getters.getCurrentUser?.username
        ) {
            return false
        }

        const navigationUsers = JSON.parse(process.env.VUE_APP_NAVIGATION_USERS)
        return navigationUsers.includes(getters.getCurrentUser.username)
    },
    hasReportingAccess(state, getters) {
        return !!getters.getCurrentUser?.reporting_access
    },
    hasRfidAccess(state, getters) {
        return !!getters.getCurrentUser?.rfid_access
    },
    hasRulesAccess(state, getters) {
        return !!getters.getCurrentUser?.rule_access
    },
    hasRunningTimeAccess(state, getters) {
        return !!getters.getCurrentUser?.running_time_access
    },
    hasSharingAccess(state, getters) {
        return !!getters.getCurrentUser?.sharing_access
    },
    hasTachographAccess(state, getters) {
        return !!getters.getCurrentUser?.tachograph_access
    },
    hasUserManagementAccess(state, getters) {
        return !!getters.getCurrentUser?.user_management_access
    },
    hasWebhookAccess(state, getters) {
        return !!getters.getCurrentUser?.webhook_access
    },
    isAccountAdmin(state) {
        const token = state.jwtOverwritten?.access || state.jwt?.access
        return token && jwtDecode(token).is_account_admin
    },
    isAdminOrStaff(state, getters) {
        return (
            getters.getCurrentUser &&
            (getters.getCurrentUser.is_account_admin ||
                getters.getCurrentUser.is_staff)
        )
    },
    isDemoUser(state, getters) {
        return (
            getters.getCurrentUser?.username ===
            process.env.VUE_APP_DEMO_USERNAME
        )
    },
    isSimulationMode(state) {
        return !!state.jwtOverwritten
    },
    isStaff(state) {
        const token = state.jwtOverwritten?.access || state.jwt?.access
        return token && jwtDecode(token).is_staff
    },
    isSuperuser(state) {
        const token = state.jwtOverwritten?.access || state.jwt?.access
        return token && jwtDecode(token).is_superuser
    },
}

const actions = {
    async loadUserInfo({ commit, getters }) {
        const userInfo = await userService.fetchUserInfo(
            getters.getCurrentUserId
        )

        localStorage.setItem('userInfo', JSON.stringify(userInfo))
        commit('setUserInfo', userInfo)
    },
    async identifyBackendFromUsername({ commit }, username) {
        const found_backends = await userService.identifyBackendFromUsername(
            username
        )
        if (found_backends.length === 1) {
            const backend = found_backends[0]
            localStorage.setItem('backend', JSON.stringify(backend))
            commit('setBackend', backend)
        }
        return found_backends
    },
    async selectBackend({ commit }, backend) {
        localStorage.setItem('backend', JSON.stringify(backend))
        commit('setBackend', backend)
    },
    async login({ commit }, { username, password }) {
        commit('resetAuth')
        const jwt = await userService.login(username, password)
        commit('setJwt', jwt)
    },
    async loginWithTokenParams({ commit }) {
        const url = new URL(window.location.href)
        const access = url.searchParams.get('access')
        const refresh = url.searchParams.get('refresh')
        if (!access || !refresh) {
            return false
        }
        url.searchParams.delete('access')
        url.searchParams.delete('refresh')
        history.replaceState(null, '', url)
        try {
            const found_backend = await userService.identifyBackendFromJWT(
                access
            )
            if (!found_backend) {
                return false
            }
            localStorage.setItem('backend', JSON.stringify(found_backend))
            commit('setBackend', found_backend)
            jwtDecode(refresh)
            localStorage.setItem('JWT', JSON.stringify({ access, refresh }))
            commit('setJwt', { access, refresh })
            return true
        } catch {
            return false
        }
    },
    logout({ commit }) {
        userService.logout()
        commit('resetAuth')
        commit('resetBackend')
    },
    async refreshTokenIfNecessary({ state, commit, dispatch }) {
        await isRefreshingToken
        commit('setIsAuthenticating', true)
        if (isTokenValid(state.jwt?.access)) {
            commit('setIsAuthenticating', false)
            return state.jwt
        } else if (isTokenValid(state.jwt?.refresh)) {
            let resolveRefreshing
            try {
                isRefreshingToken = new Promise(resolve => {
                    resolveRefreshing = () => {
                        commit('setIsAuthenticating', false)
                        isRefreshingToken = null
                        resolve()
                    }
                })
                const jwt = await userService.refreshAccessToken(
                    state.jwt.refresh
                )
                commit('setJwt', jwt)
                return jwt
            } catch {
                dispatch('logout')
            } finally {
                resolveRefreshing?.()
            }
        } else {
            commit('setIsAuthenticating', false)
            dispatch('logout')
        }
    },
    async simulateUser({ commit, state }, id) {
        try {
            router.replace({ name: router.currentRoute.name, query: {} })
        } catch (e) {
            /* ignore */
        }
        if (!state.jwtOverwritten) {
            const jwtOverwritten = localStorage.getItem('JWT')
            const userInfoOverwritten = localStorage.getItem('userInfo')

            localStorage.setItem('JWT-overwritten', jwtOverwritten)
            localStorage.setItem('userInfo-overwritten', userInfoOverwritten)

            commit('setJwtOverwritten', JSON.parse(jwtOverwritten))
        }

        const { access, refresh } = await userService.fetchUserToken(id)
        localStorage.setItem('JWT', JSON.stringify({ access, refresh }))
        commit('setJwt', { access, refresh })

        const userInfo = await userService.fetchUserInfo(id)
        localStorage.setItem('userInfo', JSON.stringify(userInfo))
        commit('setUserInfo', userInfo)
    },
    stopSimulatingUser({ commit }) {
        commit('setJwtOverwritten', null)

        const token = localStorage.getItem('JWT-overwritten')
        const userInfo = localStorage.getItem('userInfo-overwritten')

        if (!token || !userInfo) {
            return
        }

        localStorage.setItem('JWT', token)
        localStorage.setItem('userInfo', userInfo)

        localStorage.removeItem('JWT-overwritten')
        localStorage.removeItem('userInfo-overwritten')

        commit('setJwt', JSON.parse(token))
        commit('setUserInfo', JSON.parse(userInfo))
    },
}

const mutations = {
    resetAuth(state) {
        state.jwt = null
        state.jwtOverwritten = null
        state.userInfo = null
        state.isAuthenticating = false
    },
    setJwt(state, jwt) {
        state.jwt = jwt
    },
    setJwtOverwritten(state, data) {
        state.jwtOverwritten = data
    },
    resetBackend(state) {
        state.backend = null
    },
    setBackend(state, data) {
        state.backend = data
    },
    setUserInfo(state, data) {
        state.userInfo = data
    },
    setIsAuthenticating(state, data) {
        state.isAuthenticating = data
    },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
